// Global Instructions Rule Applied!

import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button } from 'antd'
import { UsergroupAddOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import type { RootState } from '../../../../state_manager/store.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { exportJsonToExcel } from '../../../../core/services/utilitiesService.ts'
import { buildReportingGridExcelPayload } from '../../../../state_manager/utils/reportingGridExcelExport.ts'
import { fetchAllFundraisingForExport } from '../../../shared/table_grid_export/fetchAllFilteredForExport.ts'

import FundraisingTable from './FundraisingTable.tsx'
import { useFundraisingData } from '../../hooks/useFundraisingData.ts'
import { useFundraisingDrawer } from '../../hooks/useFundraisingDrawer.ts'
import { useLookups } from '../../../global_configs/hooks/useLookups.ts'
import { TABLE_PROSPECTS } from '../../../../state_manager/globals.ts'
import { ProspectRecordFields } from '../../../../state_manager/data_models/prospect_record.ts'

import ViewToolbar from '../../../../core/components/ViewToolbar/index.tsx'
import type { ViewConfigColumn } from '../../../../core/components/ViewConfig/types.ts'
import { ViewConfig } from '../../../../core/components/ViewConfig/index.ts'
import { useFundraisingReportId } from '../../../../state_manager/hooks/useFundraisingReportId.ts'
import { useReportingGridColumnsWithPreferences } from '../../../../state_manager/hooks/useReportingGridColumnsWithPreferences.ts'
import { getReportingGridColumnsForReportId } from '../../../../state_manager/utils/reportingGridColumns.ts'
import { getAddColumnsCatalogForReport } from '../../../../state_manager/utils/reportingColumnCatalog.ts'
import { augmentFundraisingViewConfigColumns } from '../../utils/fundraisingViewConfigColumnAugments.ts'

export default function FundraisingPage() {
  const navigate = useNavigate()
  const fundraisingReportId = useFundraisingReportId()
  const reportingConfigs = useSelector((state: RootState) => state.globalConfigs?.reporting_configs ?? [])
  const reportingColumnDefaults = useSelector(
    (state: RootState) => state.globalConfigs?.reporting_column_defaults ?? []
  )
  const reportingColumns = useSelector((state: RootState) => state.globalConfigs?.reporting_columns ?? [])

  const {
    fundraisingFilterOptions,
    fundraisingData,
    fundraisingDataLoading,
    fundraisingFilterOptionsLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    handleFetchFilterOptions,
    handleFetchFundraising,
    handleSetFilters,
    handleSetSorting,
    handleLoadMore,
    defaultTableFilters,
    defaultTableSorting,
    handleResetFilters,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  } = useFundraisingData()

  const { getLookupOptions, getLookupLabel } = useLookups()

  const { handleProspectNameClick, handleTasksClick, handleTaskToggle } = useFundraisingDrawer(fundraisingData)

  const fundraisingGridColumnsRaw = useReportingGridColumnsWithPreferences(fundraisingReportId, preferenceRow)
  const fundraisingGridColumns = useMemo(
    () => augmentFundraisingViewConfigColumns(fundraisingGridColumnsRaw),
    [fundraisingGridColumnsRaw]
  )

  const viewConfigColumns = fundraisingGridColumns

  const addColumnsCatalog = useMemo(
    () =>
      getAddColumnsCatalogForReport(
        fundraisingReportId,
        {
          reporting_columns: reportingColumns,
          reporting_column_defaults: reportingColumnDefaults
        },
        { primaryModuleKey: 'prospects' }
      ),
    [fundraisingReportId, reportingColumns, reportingColumnDefaults]
  )

  const catalogFieldNameOrder = useMemo(
    () => [...addColumnsCatalog.deal, ...addColumnsCatalog.related].map((c) => c.valueKey),
    [addColumnsCatalog]
  )

  const defaultTableColumnKeys = useMemo(() => {
    if (!fundraisingReportId) return null
    const rows = getReportingGridColumnsForReportId(fundraisingReportId, {
      reporting_configs: reportingConfigs,
      reporting_column_defaults: reportingColumnDefaults,
      reporting_columns: reportingColumns
    })
    return rows.map((r) => r.valueKey)
  }, [fundraisingReportId, reportingConfigs, reportingColumnDefaults, reportingColumns])

  const columnByValueKey = useMemo(() => {
    const m = new Map<string, ViewConfigColumn>()
    for (const c of addColumnsCatalog.deal) m.set(c.valueKey, c)
    for (const c of addColumnsCatalog.related) m.set(c.valueKey, c)
    for (const c of viewConfigColumns) {
      if (!m.has(c.valueKey)) m.set(c.valueKey, c)
    }
    return m
  }, [addColumnsCatalog, viewConfigColumns])

  const resolveViewConfigColumn = useCallback(
    (valueKey: string) => columnByValueKey.get(valueKey),
    [columnByValueKey]
  )

  const fundraisingColumnLayout = useMemo(
    () => ({
      onPersistFieldOrder: persistColumnFieldOrder,
      onResetToDefaults: resetColumnLayoutToDefault,
      resolveColumn: resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    }),
    [
      persistColumnFieldOrder,
      resetColumnLayoutToDefault,
      resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    ]
  )

  const [exportLoading, setExportLoading] = useState(false)

  const handleExportTable = useCallback(async () => {
    setExportLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const { data, error } = await fetchAllFundraisingForExport(
        activeSorting,
        activeFilters,
        token,
        fundraisingReportId
      )
      if (error) {
        showErrorToast(error)
        return
      }
      if (data.length === 0) {
        toast.info('No rows to export')
        return
      }
      const records = data.map((r) => ({ ...r }) as Record<string, unknown>)
      const payload = buildReportingGridExcelPayload(fundraisingGridColumns, records)
      const filename = `Prospects - ${dayjs().format('YYYY-MM-DD')}.xlsx`
      const result = await exportJsonToExcel({ ...payload, filename }, token)
      if (result.success) {
        toast.success(`${filename} exported successfully`)
      } else {
        showErrorToast(result.error || 'Export failed')
      }
    } finally {
      setExportLoading(false)
    }
  }, [activeSorting, activeFilters, fundraisingReportId, fundraisingGridColumns])

  return (
    <>
      <div className='h-full overflow-hidden'>
        <div className='w-full h-full flex flex-col bg-white px-2 sm:px-4 pb-2 sm:pb-4 overflow-hidden'>
          <ViewToolbar
            moduleKey={TABLE_PROSPECTS}
            searchPlaceholder='Search Prospects...'
            recordTitleField={ProspectRecordFields.accounts__name}
            activeFilters={activeFilters}
            currentFilterOptions={fundraisingFilterOptions}
            filterOptionsLoading={fundraisingFilterOptionsLoading}
            onRefreshFilterOptions={handleFetchFilterOptions}
            onRefreshData={() => handleFetchFundraising(activeSorting, activeFilters)}
            onFilterChange={handleSetFilters}
            defaultFilters={defaultTableFilters}
            onResetFilters={handleResetFilters}
            exportEnabled
            onExport={handleExportTable}
            exportLoading={exportLoading}
            beforeAddButtonSlot={
              <Button icon={<UsergroupAddOutlined />} onClick={() => navigate('/bulk-add-prospects')}>
                Bulk Prospects
              </Button>
            }
            beforeAddSlot={
              <ViewConfig
                moduleTitle='Prospects'
                columns={viewConfigColumns}
                filterOptions={fundraisingFilterOptions}
                filterOptionsLoading={fundraisingFilterOptionsLoading}
                addColumnsCatalog={addColumnsCatalog}
                addColumnsTabLabels={{ primary: 'Prospect', related: 'Related' }}
                columnLayout={fundraisingColumnLayout}
                filteringConfig={{
                  currentFilters: activeFilters,
                  defaultFilters: defaultTableFilters,
                  onApplyFilters: async (filters) => {
                    handleSetFilters(filters)
                  },
                  onResetToDefaults: async () => {
                    handleSetFilters(defaultTableFilters)
                  }
                }}
                sortingConfig={{
                  currentSorting: activeSorting,
                  defaultSorting: defaultTableSorting,
                  onApplySorting: async (sorting) => {
                    handleSetSorting(sorting)
                  },
                  onResetToDefaults: async () => {
                    handleSetSorting(defaultTableSorting)
                  }
                }}
              />
            }
          />

          <div className='flex-1 min-h-0 h-full flex flex-col'>
            <FundraisingTable
              loading={fundraisingDataLoading}
              loadingMore={loadingMore}
              hasMore={hasMore}
              currentPage={currentPage}
              activeFilters={activeFilters}
              activeSorting={activeSorting}
              filterOptions={fundraisingFilterOptions}
              filterOptionsLoading={fundraisingFilterOptionsLoading}
              gridColumns={fundraisingGridColumns}
              onLoadMore={handleLoadMore}
              onFiltersChange={(filters) => {
                const filterArray = Object.entries(filters).map(([key, value]) => ({
                  key,
                  value
                }))
                handleSetFilters(filterArray)
              }}
              onSortChange={(sortConfig: any) => {
                if (sortConfig) {
                  handleSetSorting([{ key: sortConfig.column, direction: sortConfig.direction }])
                } else {
                  handleSetSorting([])
                }
              }}
              onProspectNameClick={handleProspectNameClick}
              onTasksClick={handleTasksClick}
              onTaskToggle={handleTaskToggle}
              getLookupOptions={getLookupOptions}
              getLookupLabel={getLookupLabel}
            />
          </div>
        </div>
      </div>
    </>
  )
}
